import { createContext, type RefObject, useContext } from "react";

export enum ReplyPlacement {
  floating = "floating",
  inline = "inline",
}

export interface OpenDraftOptions {
  sourceConversationId?: string | null;
  placement?: ReplyPlacement;
  preserveExistingPlacement?: boolean;
}

export interface DraftsComposeContextValue {
  openDrafts: (draftIds: string[], options?: OpenDraftOptions) => void;
  openDraft: (draftId: string, options?: OpenDraftOptions) => void;
  closeDrafts: (draftIds: string[]) => void;
  closeDraft: (draftId: string) => void;
  canReturnDraftInline: (draftId: string) => boolean;
  returnDraftInline: (draftId: string) => boolean;
  drafts: string[];
  floatingDrafts: string[];
  getInlineDraftsForConversation: (conversationId?: string | null) => string[];
  getDraftPlacementForConversation: (conversationId?: string | null) => ReplyPlacement;
  draftsContainerRef: RefObject<HTMLDivElement | null>;
  /** Records one attachment upload as in flight for a draft. Call the returned release once it settles. */
  beginPendingAttachmentUpload: (draftId: string) => () => void;
  /** Attachment uploads started for a draft but not yet persisted to it. */
  getPendingAttachmentUploadCount: (draftId: string) => number;
}

export const DraftsComposeContext = createContext<DraftsComposeContextValue | null>(null);

export const useDraftsCompose = () => {
  const context = useContext(DraftsComposeContext);

  if (!context) {
    throw new Error("useDraftsCompose must be used within a DraftsComposeProvider");
  }

  return context;
};
